import type {TFunction} from "i18next";
import {TabPanel} from "primereact/tabview";
import {Tooltip} from "primereact/tooltip";
import type {KeyboardEvent, MouseEvent, ReactElement, ReactNode} from "react";

import {checkPermissionRule, type PermissionRule} from "../util/permissions";
import type {RevaPermission} from "../util/revaPermission";
import MissingPermissionTooltip from "./MissingPermissionTooltip";
import {getResourceColor} from "./paramInput/paramHooks";

export interface TabHeaderWithPermissionParams {
    /** the tab header i18nKey to display */
    headerI18n: string;
    /** the color key to get header text color */
    colorKey?: string;
    key?: string;
    date?: string;
    /** if key is not defined, use route as implicit key */
    route?: string;
    /** if defined, use this value for the tab-value of the appreciation key (if empty, don't show appreciation) */
    appreciationKeyTab?: string;
    /** flag, if a tooltip should be disabled */
    hasTooltip?: boolean;
    /** flag, if tab should be disabled */
    disabled?: boolean;
    /** permission to check */
    permissionRule?: PermissionRule | null;
    /** optional indicator (e.g. a warning badge) rendered next to the tab header text */
    badge?: ReactNode;
}

const headerTemplate = (header: ReactNode, key: string | undefined) => (
    <>
        <a
            role="tab"
            className={`p-tabview-nav-link tabHeader-${key}`}
            tabIndex={-1}
            aria-selected="false"
            aria-disabled="true"
            data-pc-section="headeraction"
            style={{pointerEvents: "all"}}
        >
            <span className="pi pi-lock mr-2"></span>
            <span className="p-tabview-title title-font-size" data-pc-section="headertitle">
                {header}
            </span>
        </a>
        <MissingPermissionTooltip target={`.tabHeader-${key}`} position="top" />
    </>
);

const getHeader = (
    header: ReactNode,
    key: string | undefined,
    color: string,
    tooltip: ReactNode,
    badge?: ReactNode,
) => (
    <>
        <span
            id={`tabHeader_${String(key).replace(/-/g, "_")}`}
            className={`text-${color} tabHeader-${key} title-font-size`}
            data-pr-at="right bottom"
        >
            {header}
        </span>
        {badge}
        {tooltip && (
            <Tooltip target={`.tabHeader-${key}`} className="custom-tooltip">
                {tooltip}
            </Tooltip>
        )}
    </>
);

/**
 * Get `<TabPanel/>` with permission check
 * @param tab tab properties
 * @param t translation function
 * @param privileges loaded privileges of user
 */
export function getTabPanelWithPermission(
    tab: TabHeaderWithPermissionParams,
    t: TFunction,
    privileges: RevaPermission[],
): ReactElement {
    let permitted = true;
    if (tab.permissionRule) {
        permitted = checkPermissionRule(privileges, tab.permissionRule);
    }
    const header = t(tab.headerI18n, {date: tab.date});
    const tooltip = tab.hasTooltip ? t(`${tab.headerI18n}_tooltip`) : null;
    const key = tab.key || tab.route;
    // immodrop's soft grey doesn't read any darker than a regular unstyled tab here, so use the normal variant
    const color = getResourceColor(tab.colorKey, false, tab.colorKey !== "immodrop");

    if (!permitted) {
        return <TabPanel key={key} header={header} headerTemplate={headerTemplate(header, key)} disabled />;
    }
    return <TabPanel key={key} header={getHeader(header, key, color, tooltip, tab.badge)} disabled={!!tab.disabled} />;
}

/**
 * `<TabPanel/>` für den angedeuteten "+"-Tab am Ende der Tab-Liste: er blendet die optionalen,
 * gerade versteckten Tabs per Menü ein (#19159).
 *
 * Der Panel ist `disabled`, damit PrimeReact ihn nie als aktiven Tab auswählt und die Ink-Bar nicht
 * springt; der Klick kommt deshalb — wie beim gesperrten Tab oben — aus dem eigenen `headerTemplate`.
 * @param t translation function
 * @param onClick Öffner für das Menü, bekommt das Klick-Event als Anker
 */
export function getMoreTabPanel(t: TFunction, onClick: (event: MouseEvent | KeyboardEvent) => void): ReactElement {
    const header = t("object.category.more");
    return (
        <TabPanel
            key="more"
            header={header}
            disabled
            headerTemplate={
                <>
                    <a
                        id="tabHeader_more"
                        role="tab"
                        className="p-tabview-nav-link tabHeader-more"
                        tabIndex={0}
                        aria-selected="false"
                        data-pc-section="headeraction"
                        style={{pointerEvents: "all"}}
                        onClick={onClick}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") onClick(event);
                        }}
                    >
                        <span className="p-tabview-title title-font-size" data-pc-section="headertitle">
                            {header}
                        </span>
                    </a>
                    <Tooltip target=".tabHeader-more" position="top" className="custom-tooltip">
                        {t("object.category.more_tooltip")}
                    </Tooltip>
                </>
            }
        />
    );
}
